import { Component, Input, ViewChild, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { Menu } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { GlobalMessageService } from '../../global-services/global-message.service';
import { CrudTreatmentComponent } from '../../features/crud-treatment/crud-treatment.component';
import { COMMON_IMPORTS } from '../../global-misc/common-imports';
import { PremoService } from '../../global-services/premo.service';
import { Observable } from 'rxjs';
import { PatientService } from '../../global-services/patient.service';

export enum ActionMenuEvent {
  ADD_TREATMENT,
  PATIENT_DELETED,
}

@Component({
  standalone: true,
  selector: 'app-patient-actions-menu',
  imports: [...COMMON_IMPORTS, ButtonModule, MenuModule, TooltipModule, DialogModule, CrudTreatmentComponent],
  templateUrl: './patient-actions-menu.template.html',
})
export class PatientActionsMenuComponent {
  @Input() orgId!: string;
  @Input() patientId!: string;

  private _menuEvent: EventEmitter<ActionMenuEvent> = new EventEmitter<ActionMenuEvent>();
  @Output() menuEvent: Observable<ActionMenuEvent> = this._menuEvent.asObservable();

  @ViewChild('menu') menu!: Menu;

  public showTreatmentDialog = false; // CHANGE ME

  constructor(
    private readonly router: Router,
    private readonly premoService: PremoService,
    private readonly messageService: GlobalMessageService,
    private readonly patientService: PatientService,
  ) {}

  public getActionItems(): MenuItem[] {
    const items: MenuItem[] = [];

    items.push({
      label: 'View Patient',
      icon: 'pi pi-eye',
      command: () => this.viewPatient(),
    });

    items.push({
      label: 'Add PreMO Visit',
      icon: 'pi pi-calendar-plus',
      command: () => this.addPremoVisit(),
    });

    items.push({
      label: 'Calculate PreMO',
      icon: 'pi pi-calculator',
      command: () => this.calculatePremo(),
    });

    items.push({
      label: 'Add Treatment',
      icon: 'pi pi-plus-circle',
      command: () => this.addTreatment(),
    });

    items.push({
      label: 'Delete Patient',
      icon: 'pi pi-user-minus',
      command: () => this.deletePatient(),
    });

    return items;
  }

  public toggleActionsMenu(event: Event) {
    this.menu.model = this.getActionItems();
    this.menu.toggle(event);

    // Prevent the menu from appearing outside the viewport
    event.preventDefault();
    event.stopPropagation();
  }

  private viewPatient() {
    this.router.navigate(['/orgs', this.orgId, 'patients', this.patientId, 'manage']);
  }

  private assignForm() {
    this.router.navigate(['/orgs', this.orgId, 'patients', this.patientId, 'form-assignments', 'create']);
  }

  private addPremoVisit() {
    this.router.navigate(['/orgs', this.orgId, 'patients', this.patientId, 'visit', 'create']);
  }

  private async calculatePremo() {
    if (!(await this.premoService.canCalculatePremo(this.orgId, this.patientId))) {
      this.messageService.showError('Calculate Premo Error', "Can't calculate premo as patient has no visits.");
      return;
    }
    this.router.navigate(['/orgs', this.orgId, 'patients', this.patientId, 'calculate-premo']);
  }

  private addTreatment() {
    this.showTreatmentDialog = true;
  }

  private async deletePatient() {
    try {
      await this.patientService.deletePatient(this.orgId, this.patientId);
      this.messageService.showSuccess('Patient Deleted', 'Patient has been successfully deleted.');

      const currentUrl = this.router.url;
      if (currentUrl.includes('/patients/manage')) {
        this._menuEvent.emit(ActionMenuEvent.PATIENT_DELETED);
      } else if (currentUrl.includes(`/patients/${this.patientId}/manage`)) {
        this.router.navigate(['/orgs', this.orgId, 'patients', 'manage']);
      }
    } catch (error) {
      this.messageService.showError('Delete Error', 'Failed to delete patient. Please try again.');
    }
  }

  public onTreatmentSubmit() {
    this.showTreatmentDialog = false;
    this._menuEvent.emit(ActionMenuEvent.ADD_TREATMENT);
  }
}
